import { useEffect, useRef, useState } from "react";
import QRCode from "qrcode";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { fetchOrder } from "../lib/api";
import { fmtARS, STATUS_LABEL } from "../lib/utils-app";
import {
  CheckCircle2,
  Copy,
  ExternalLink,
  Loader2,
  MessageCircle,
  Smartphone,
  XCircle,
} from "lucide-react";
import { toast } from "sonner";

export default function PaymentQRDialog({ open, onClose, order, themeColor = "#ff4500" }) {
  const [qrDataUrl, setQrDataUrl] = useState(null);
  const [status, setStatus] = useState(order?.status || "pending_payment");
  const [current, setCurrent] = useState(order);
  const pollRef = useRef(null);

  useEffect(() => {
    if (!open || !order?.checkout_url) return;
    QRCode.toDataURL(order.checkout_url, {
      width: 320,
      margin: 1,
      color: { dark: "#000000", light: "#FFFFFFFF" },
      errorCorrectionLevel: "M",
    })
      .then(setQrDataUrl)
      .catch(() => setQrDataUrl(null));
  }, [open, order]);

  useEffect(() => {
    if (!open || !order?.order_id) return;
    const tick = async () => {
      try {
        const o = await fetchOrder(order.order_id);
        setCurrent(o);
        setStatus(o.status);
      } catch {
        // silent
      }
    };
    tick();
    pollRef.current = setInterval(tick, 3000);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [open, order]);

  useEffect(() => {
    if (["paid", "processing", "completed", "failed"].includes(status)) {
      if (pollRef.current) clearInterval(pollRef.current);
    }
  }, [status]);

  if (!order) return null;
  const isSuccess = ["paid", "processing", "completed"].includes(status);
  const isFail = status === "failed";
  const productLabel =
    order.product_name
      ? (order.quantity > 1 ? `${order.quantity} × ${order.product_name}` : order.product_name)
      : order.diamonds
        ? `${order.diamonds.toLocaleString("es-AR")} Diamantes`
        : order.robux
          ? `${order.robux.toLocaleString("es-AR")} Robux`
          : "Pedido";
  const whatsappLink = `https://wa.me/543821452181?text=${encodeURIComponent(
    `Hola! Adjunto comprobante del pedido ${order.order_id} (${productLabel}) para ${order.player_id}`
  )}`;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        data-testid="payment-qr-dialog"
        className="bg-[#0d0d0d] text-white max-w-md p-0 overflow-hidden"
        style={{ border: `1px solid ${themeColor}55` }}
      >
        <div className="p-6">
          <DialogTitle className="font-display text-3xl tracking-wider">
            Pagá con Mercado Pago
          </DialogTitle>
          <DialogDescription className="text-neutral-400 text-sm">
            Escaneá el QR con tu cámara o abrí el link en tu celular.
          </DialogDescription>

          {isSuccess ? (
            <div className="text-center py-6">
              <CheckCircle2 className="w-14 h-14 text-emerald-400 mx-auto" />
              <div className="font-display text-3xl tracking-wider mt-3">
                ¡Pago recibido!
              </div>
              <p className="text-neutral-400 text-sm mt-1">
                Estamos ejecutando tu carga: {productLabel}.
              </p>
              {order.extra_instructions && (
                <a
                  href={whatsappLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="qr-whatsapp-btn"
                  className="mt-4 inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-emerald-500 hover:bg-emerald-400 text-black font-semibold"
                >
                  <MessageCircle className="w-4 h-4" /> Enviar comprobante por WhatsApp
                </a>
              )}
            </div>
          ) : isFail ? (
            <div className="text-center py-8">
              <XCircle className="w-14 h-14 text-rose-400 mx-auto" />
              <div className="font-display text-3xl tracking-wider mt-3">
                Pago no completado
              </div>
              <p className="text-neutral-400 text-sm mt-1">
                Podés reintentar generando un nuevo pedido.
              </p>
            </div>
          ) : (
            <>
              <div className="mt-5 grid place-items-center">
                <div className="rounded-xl bg-white p-3" style={{ filter: `drop-shadow(0 0 8px ${themeColor}88)` }}>
                  {qrDataUrl ? (
                    <img
                      src={qrDataUrl}
                      alt="QR Mercado Pago"
                      className="w-64 h-64"
                      data-testid="mp-qr-image"
                    />
                  ) : (
                    <div className="w-64 h-64 grid place-items-center text-neutral-500">
                      <Loader2 className="w-6 h-6 animate-spin" />
                    </div>
                  )}
                </div>
              </div>

              <div className="mt-5 grid grid-cols-2 gap-3 text-sm">
                <InfoBox label="Total" value={fmtARS(order.amount_ars)} />
                <InfoBox label="Producto" value={productLabel} />
                <InfoBox label="Identificador" value={order.player_id} mono />
                <InfoBox label="Estado" value={STATUS_LABEL[status] || status} />
              </div>

              {order.extra_instructions && (
                <div className="mt-4 rounded-md p-3 text-xs border" style={{ background: "#0a0a0a", borderColor: `${themeColor}55`, color: "#e5e5e5" }}>
                  <div className="flex items-start gap-2">
                    <MessageCircle className="w-4 h-4 mt-0.5 shrink-0" style={{ color: themeColor }} />
                    <span>{order.extra_instructions}</span>
                  </div>
                </div>
              )}

              <div className="mt-5 flex flex-col sm:flex-row gap-2">
                <Button
                  asChild
                  data-testid="open-mp-mobile-btn"
                  className="flex-1 font-bold text-black"
                  style={{ background: themeColor }}
                >
                  <a href={order.checkout_url} target="_blank" rel="noopener noreferrer">
                    <Smartphone className="w-4 h-4 mr-2" /> Abrir en móvil
                  </a>
                </Button>
                <Button
                  onClick={() => {
                    navigator.clipboard?.writeText(order.checkout_url);
                    toast.success("Link copiado");
                  }}
                  data-testid="copy-mp-link-btn"
                  variant="outline"
                  className="bg-transparent hover:bg-white/5 text-white"
                  style={{ borderColor: `${themeColor}55` }}
                >
                  <Copy className="w-4 h-4 mr-2" /> Copiar link
                </Button>
              </div>

              <div className="mt-4 flex items-center gap-2 text-xs text-neutral-500">
                <Loader2 className="w-3 h-3 animate-spin" />
                Esperando confirmación del pago...
              </div>
            </>
          )}

          <button
            onClick={onClose}
            data-testid="close-qr-dialog-btn"
            className="mt-5 w-full text-xs text-neutral-500 hover:text-white py-2"
          >
            {isSuccess ? "Ir a mis pedidos" : "Cerrar y volver"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function InfoBox({ label, value, mono }) {
  return (
    <div className="rounded-md border border-white/10 bg-black/40 p-2.5">
      <div className="text-[10px] uppercase tracking-widest text-neutral-500">{label}</div>
      <div className={`text-sm text-white ${mono ? "font-mono" : ""}`}>{value}</div>
    </div>
  );
}
